import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Info } from 'lucide-react';
import API_URL from '../../config';

// Avsnitt som är skrivna för utvecklare, inte användare (beslut Mikael 2026-10-13).
const TEKNISKT = /teknisk|api\b|datamodell|workflow|roadmap|begränsning|objekt\s*&\s*fält|metadata|endpoint/i;

const cache = new Map();

function rensa(text) {
  let rader = String(text || '').split(/\r?\n/);
  if (rader[0] && rader[0].trim() === '---') {
    const slut = rader.indexOf('---', 1);
    if (slut > 0) rader = rader.slice(slut + 1);
  }
  const ut = [];
  let hoppa = false;
  let iKod = false;
  rader.forEach((rad) => {
    // Kodblock (JSON, API-anrop) är skrivna för utvecklare och tas alltid bort.
    if (/^\s*```/.test(rad)) { iKod = !iKod; return; }
    if (iKod) return;
    const rubrik = /^(#{1,3})\s+(.*)$/.exec(rad.trim());
    if (rubrik) {
      const niva = rubrik[1].length;
      const namn = rubrik[2].replace(/\s*\[tag:[^\]]+\]/g, '').replace(/^\d+(\.\d+)*\.?\s+/, '').trim();
      if (niva === 1) return; // modulnamnet står redan i hjälpvyns rubrik
      if (niva === 2) hoppa = TEKNISKT.test(namn);
      if (!hoppa) ut.push(`${rubrik[1]} ${namn}`);
      return;
    }
    if (!hoppa) ut.push(rad.replace(/\s*\[tag:[^\]]+\]/g, ''));
  });
  return ut.join('\n');
}

export function rubrikId(text) {
  return `besk-${String(text).toLowerCase().replace(/[^a-z0-9åäö]+/g, '-').replace(/^-|-$/g, '')}`;
}

/** Rubrikerna (nivå 2–3) i en rensad beskrivning, för innehållslistan. */
export function rubriker(text) {
  return String(text || '').split('\n')
    .map((r) => /^(#{2,3})\s+(.*)$/.exec(r))
    .filter(Boolean)
    .map((m) => ({ rubrik: m[2], id: rubrikId(m[2]) }));
}

/**
 * Den korta modulbeskrivningen för en modul som saknar instruktion — rensad
 * från taggar, metadata och tekniska avsnitt.
 */
export default function Beskrivning({ modul, onText }) {
  const [text, setText] = useState(cache.get(modul && modul.source) || '');
  const [fel, setFel] = useState(false);

  useEffect(() => {
    if (!modul || !modul.source) return undefined;
    if (cache.has(modul.source)) { setText(cache.get(modul.source)); return undefined; }
    let avbruten = false;
    setFel(false);
    setText('');
    const adress = modul.source.startsWith('/docs/') ? `${API_URL}${modul.source}` : modul.source;
    fetch(adress)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => { const r = rensa(t); cache.set(modul.source, r); if (!avbruten) setText(r); })
      .catch(() => { if (!avbruten) setFel(true); });
    return () => { avbruten = true; };
  }, [modul]);

  useEffect(() => { if (onText) onText(text); }, [text, onText]);

  const komponenter = useMemo(() => ({
    h2: ({ children }) => <h3 id={rubrikId(String(children))}>{children}</h3>,
    h3: ({ children }) => <h4 id={rubrikId(String(children))}>{children}</h4>,
  }), []);

  return (
    <div className="hjalp-beskrivning">
      <div className="hjalp-notis">
        <Info size={16} strokeWidth={2} aria-hidden="true" />
        <div>
          <b>Det finns ingen instruktion för {modul ? modul.title : 'modulen'} ännu.</b>{' '}
          Här visas den korta modulbeskrivningen så länge. När en instruktion tas fram visas den här automatiskt.
        </div>
      </div>
      <div className="hjalp-beskrivning-text">
        {fel && <p>Modulbeskrivningen kunde inte hämtas.</p>}
        {!fel && !text && <div className="instr-skel" style={{ height: 240, marginTop: 16 }} />}
        {!fel && text && <ReactMarkdown remarkPlugins={[remarkGfm]} components={komponenter}>{text}</ReactMarkdown>}
      </div>
    </div>
  );
}
